import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, LinkButton, Notice, TextField, buttonClass } from './ui.js';

type Kind = 'slack' | 'webhook';
interface Links {
  connectUrl: string;
  connectGroupUrl: string;
}

/**
 * Immediate alerts to Telegram, Slack or an own endpoint (T-69). Recipients.tsx lists every
 * channel; this card adds them and shows the one-time data (Telegram link, webhook secret).
 */
export function ChatChannelsCard() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const channels = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => unwrap(api.GET('/alert-channels')),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['alert-channels'] });
  const [form, setForm] = useState<Kind>();
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [links, setLinks] = useState<Links>();
  const [secret, setSecret] = useState<string>();
  const [error, setError] = useState<string>();

  const failed = (e: unknown) =>
    setError(
      e instanceof ApiError && e.code === 'PLAN_LIMIT_REACHED' && e.problem.limit?.max
        ? t('notifications.limitReached', { max: e.problem.limit.max })
        : errorMessage(e, t),
    );
  const telegram = useMutation({
    mutationFn: () => unwrap(api.POST('/alert-channels', { body: { type: 'telegram' } })),
    onSuccess: (c) => {
      if (c.connectUrl && c.connectGroupUrl)
        setLinks({ connectUrl: c.connectUrl, connectGroupUrl: c.connectGroupUrl });
      void refresh();
    },
    onError: failed,
  });
  const relink = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/alert-channels/{channelId}/telegram-link', {
          params: { path: { channelId: id } },
        }),
      ),
    onSuccess: setLinks,
    onError: failed,
  });
  const add = useMutation({
    mutationFn: (kind: Kind) =>
      unwrap(
        api.POST('/alert-channels', {
          body: { type: kind, url: url.trim(), ...(label.trim() ? { label: label.trim() } : {}) },
        }),
      ),
    onSuccess: (c) => {
      setSecret(c.signingSecret);
      setForm(undefined);
      setUrl('');
      setLabel('');
      void refresh();
    },
    onError: failed,
  });

  const data = channels.data;
  if (!data) return null;
  const { chat } = data;
  const pending = data.items.filter((c) => c.type === 'telegram' && !c.verified);
  const open = (kind?: Kind) => {
    setError(undefined);
    setSecret(undefined);
    setLinks(undefined);
    setForm(kind);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (form) add.mutate(form);
  };

  return (
    <Card>
      <h2 className="text-xl font-semibold">{t('chat.title')}</h2>
      <p className="mt-1 max-w-prose text-slate-800">{t('chat.intro')}</p>
      {!chat.slack ? (
        <div className="mt-3">
          <Notice kind="info">
            {t('chat.planNeeded')}{' '}
            <LinkButton to="/billing" variant="soft" size="sm">
              {t('chat.upgrade')}
            </LinkButton>
          </Notice>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            {chat.telegram ? (
              <Button
                variant="secondary"
                busy={telegram.isPending}
                onClick={() => {
                  open();
                  telegram.mutate();
                }}
              >
                {t('chat.telegram')}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => open('slack')}>
              {t('chat.slack')}
            </Button>
            {chat.webhook ? (
              <Button variant="secondary" onClick={() => open('webhook')}>
                {t('chat.webhook')}
              </Button>
            ) : null}
          </div>
          {!chat.webhook ? (
            <p className="mt-2 text-sm text-slate-700">{t('chat.webhookPlan')}</p>
          ) : null}
        </>
      )}

      <div aria-live="polite" className="mt-3 flex flex-col gap-3">
        {error ? <Notice kind="error">{error}</Notice> : null}
        {links ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            <p className="font-semibold">{t('chat.telegramSteps')}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={links.connectUrl}
                target="_blank"
                rel="noreferrer"
                className={buttonClass('primary')}
              >
                {t('chat.telegramPrivate')}
              </a>
              <a
                href={links.connectGroupUrl}
                target="_blank"
                rel="noreferrer"
                className={buttonClass('soft')}
              >
                {t('chat.telegramGroup')}
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-700">{t('chat.telegramHint')}</p>
            <div className="mt-3">
              <Button variant="secondary" size="sm" onClick={() => void refresh()}>
                {t('chat.telegramDone')}
              </Button>
            </div>
          </div>
        ) : null}
        {secret ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
            <p className="font-semibold">{t('chat.secretTitle')}</p>
            <code className="mt-2 block break-all rounded bg-white p-2 font-mono text-sm">
              {secret}
            </code>
            <p className="mt-2 text-sm text-slate-800">{t('chat.secretHint')}</p>
          </div>
        ) : null}
      </div>

      {pending.length > 0 && !links ? (
        <ul className="mt-3 flex flex-col gap-2">
          {pending.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span>{t('chat.waiting')}</span>
              <Button
                variant="secondary"
                size="sm"
                busy={relink.isPending}
                onClick={() => relink.mutate(c.id)}
              >
                {t('chat.showLink')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {form ? (
        <form
          onSubmit={submit}
          noValidate
          className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4"
        >
          <TextField
            label={t(`chat.${form}Url`)}
            hint={t(`chat.${form}UrlHint`)}
            type="url"
            autoComplete="off"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <TextField
            label={t('chat.label')}
            hint={t(`chat.${form}LabelHint`)}
            optional
            maxLength={80}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" busy={add.isPending}>
              {t('notifications.add')}
            </Button>
            <Button variant="secondary" onClick={() => open()}>
              {t('app.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
